@if (loading()) {
<app-page-loader loadingType="content" />
} @else {
<app-survey-header [survey]="survey"></app-survey-header>
<div class="p-3 h-[calc(100%-var(--spacing)*15)]">
  <div class="flex mb-2">
    <app-breadcrumb class="flex-1" [items]="[{ label: 'Surveys', route: '/surveys' },{ label: 'Survey CMD' }]"/>
    @if (canEdit()) {
    <div class="flex items-center gap-2">
      @if (pendingChangesCount > 0) {
      <button type="button" (click)="requestDiscard()"
          class="rounded-lg border border-gray-400 bg-white px-3 py-1.5 text-md font-medium text-gray-600 hover:bg-gray-100">
        Discard
      </button>
      }
      <button type="button" [disabled]="pendingChangesCount == 0 || hasValidationErrors" (click)="requestSave()"
          class="rounded-lg border border-blue-600 bg-blue-600 px-4 py-2 text-lg font-medium text-white hover:bg-white hover:text-blue-600
          disabled:opacity-50 disabled:bg-white disabled:border-gray-400 disabled:text-gray-600 disabled:hover:text-gray-600 disabled:cursor-not-allowed">
        Save
        @if (pendingChangesCount > 0) {
        <span class="text-xs">
          ({{ pendingChangesCount }} change{{ pendingChangesCount === 1 ? '' : 's' }})
        </span>
        }
      </button>
    </div>
    }
  </div>

  <div class="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="border-b border-stroke px-5 py-4 dark:border-strokedark sm:px-7.5">
      <h4 class="text-xl font-semibold text-black dark:text-white">
        {{ survey?.id }} <span class="text-lg text-gray-700">CMD Config</span>
      </h4>
    </div>

    <!-- Tabs -->
    <div class="border-b border-stroke px-5 dark:border-strokedark sm:px-7.5">
      <div class="flex gap-6 overflow-x-auto">
        <button
          type="button"
          (click)="setActiveTab('csv')"
          class="border-b-2 py-4 text-sm font-medium"
          [class.border-blue-600]="activeTab() === 'csv'"
          [class.text-blue-600]="activeTab() === 'csv'"
          [class.border-transparent]="activeTab() !== 'csv'"
          [class.text-bodydark2]="activeTab() !== 'csv'"
        >
          Table Format
        </button>

        <button
          type="button"
          (click)="setActiveTab('json')"
          class="border-b-2 py-4 text-sm font-medium"
          [class.border-blue-600]="activeTab() === 'json'"
          [class.text-blue-600]="activeTab() === 'json'"
          [class.border-transparent]="activeTab() !== 'json'"
          [class.text-bodydark2]="activeTab() !== 'json'"
        >
          JSON Format
        </button>
      </div>
    </div>

    <!-- Tab content -->
    <div class="p-5 sm:p-7.5">
      @if (activeTab() === 'csv') {
        @if (canEdit()) {
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <button type="button" (click)="addRow()"
              class="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
            + Add Column
          </button>
          <div class="flex flex-col items-end gap-1 text-xs">
            @if (hasEmptyDbColumn) {
              <span class="text-error-600">All columns must have a dbColumn name.</span>
            }
            @if (duplicateKeys.size > 0) {
              <span class="text-error-600">Duplicate csvPosition + dbColumn in rows: {{ duplicateKeysText }}</span>
            }
            @if (hasInvalidJson) {
              <span class="text-error-600">One or more columnValueTranslation values contain invalid JSON.</span>
            }
          </div>
        </div>
        }
        <div class="max-h-[65vh] overflow-auto rounded-lg border border-gray-300 bg-white shadow-sm">
            <table class="min-w-300 border-collapse text-sm">
              <thead class="sticky top-0 z-10">
                <tr class="bg-[#f3f3f3]">
                  @if (canEdit()) {
                  <th class="min-w-25 border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">Actions</th>
                  }
                  <th class="min-w-15 border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">#</th>
                  <th class="min-w-25 border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">csvPosition</th>
                  <th class="min-w-40 border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">dbColumn</th>
                  <th class="min-w-20 border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">additionalData</th>
                  <th class="min-w-20 border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">isMandatory</th>
                  <th class="min-w-20 border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">isHashColumn</th>
                  <th class="min-w-40 border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                    <span class="flex items-center gap-1">
                      calculatedColumnRule
                      <app-info-tooltip>This value is not editable.</app-info-tooltip>
                    </span>
                  </th>
                  <th class="min-w-40 border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">defaultColumnValue</th>
                  <th class="min-w-30 border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">format</th>
                  <th class="min-w-30 border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">optionalFormat</th>
                  <th class="min-w-30 border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">outFormat</th>
                  <th class="min-w-20 border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">useCustomerLanguagePreference</th>
                  <th class="min-w-20 border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">isPhoneContact</th>
                  <th class="min-w-20 border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">ignoreHash</th>
                  <th class="min-w-50 border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">columnValueTranslation</th>
                </tr>
              </thead>

              <tbody>
                @for (row of rows; track row; let i = $index) {
                  <tr class="hover:bg-[#e8f0fe]"
                      [class.bg-success-50]="getRowChangeStatus(row) === 'new'"
                      [class.bg-warning-50]="getRowChangeStatus(row) === 'modified'"
                      [class.bg-error-50]="getRowChangeStatus(row) === 'deleted'"
                      [class.opacity-60]="getRowChangeStatus(row) === 'deleted'">
                    @if (canEdit()) {
                    <td class="border border-gray-300 bg-[#f8f8f8] px-2 py-2 text-center">
                      <div class="flex flex-col items-center gap-1">
                        <button type="button" (click)="toggleDelete(row)"
                            class="rounded border px-2 py-0.5 text-xs font-medium"
                            [class.border-error-600]="!row.markedForDelete"
                            [class.text-error-600]="!row.markedForDelete"
                            [class.hover:bg-error-50]="!row.markedForDelete"
                            [class.border-gray-400]="row.markedForDelete"
                            [class.text-gray-600]="row.markedForDelete">
                          {{ row.markedForDelete ? 'Undo' : 'Delete' }}
                        </button>
                        @switch (getRowChangeStatus(row)) {
                          @case ('new') { <span class="text-[10px] font-semibold text-success-600">NEW</span> }
                          @case ('modified') { <span class="text-[10px] font-semibold text-warning-600">MODIFIED</span> }
                          @case ('deleted') { <span class="text-[10px] font-semibold text-error-600">DELETED</span> }
                        }
                      </div>
                    </td>
                    }
                    @if (isDuplicateKey(row)) {
                    <td class="border border-gray-300 bg-error-100 px-3 py-2 text-center font-semibold text-error-700" title="Same csvPosition + dbColumn as another row">{{ i + 1 }}</td>
                    } @else {
                    <td class="border border-gray-300 bg-[#f8f8f8] px-3 py-2 text-center text-gray-600">{{ i + 1 }}</td>
                    }
                    <td class="border border-gray-300 px-2 py-1 text-gray-800">
                      <input type="number" [(ngModel)]="row.model.csvPosition" [disabled]="!canEdit() || row.markedForDelete"
                        class="w-full rounded border border-transparent px-1.5 py-1 focus:border-gray-300 focus:bg-[#fff2cc] disabled:bg-transparent disabled:text-gray-500"/>
                    </td>
                    <td class="border border-gray-300 px-2 py-1 text-gray-800">
                      <input #dbColumnInput type="text" [(ngModel)]="row.model.dbColumn" [disabled]="!canEdit() || row.markedForDelete"
                        class="w-full rounded border border-transparent px-1.5 py-1 focus:border-gray-300 focus:bg-[#fff2cc] disabled:bg-transparent disabled:text-gray-500"
                        [class.border-error-500]="!row.model.dbColumn.trim()"/>
                    </td>
                    <td class="border border-gray-300 px-3 py-2 text-center">
                      <input type="checkbox" [(ngModel)]="row.model.additionalData" [disabled]="!canEdit() || row.markedForDelete"/>
                    </td>
                    <td class="border border-gray-300 px-3 py-2 text-center">
                      <input type="checkbox" [(ngModel)]="row.model.isMandatory" [disabled]="!canEdit() || row.markedForDelete"/>
                    </td>
                    <td class="border border-gray-300 px-3 py-2 text-center">
                      <input type="checkbox" [(ngModel)]="row.model.isHashColumn" [disabled]="!canEdit() || row.markedForDelete"/>
                    </td>
                    <!-- Read-only: the rule is a complex object, not something to hand-edit in a cell. -->
                    <td class="border border-gray-300 px-2 py-1 align-top text-gray-800">
                      @if (row.model.calculatedColumnRule === null || row.model.calculatedColumnRule === '') {
                        <span class="px-1.5 text-gray-400">null</span>
                      } @else if (isObject(row.model.calculatedColumnRule)) {
                        <pre class="max-h-40 max-w-sm overflow-auto rounded bg-gray-50 px-1.5 py-1 font-mono text-[11px] leading-snug text-gray-700">{{ row.model.calculatedColumnRule | json }}</pre>
                      } @else {
                        <span class="px-1.5">{{ row.model.calculatedColumnRule }}</span>
                      }
                    </td>
                    <td class="border border-gray-300 px-2 py-1 text-gray-800">
                      <input type="text" [(ngModel)]="row.model.defaultColumnValue" [disabled]="!canEdit() || row.markedForDelete"
                        class="w-full rounded border border-transparent px-1.5 py-1 focus:border-gray-300 focus:bg-[#fff2cc] disabled:bg-transparent disabled:text-gray-500"/>
                    </td>
                    <td class="border border-gray-300 px-2 py-1 text-gray-800">
                      <input type="text" [(ngModel)]="row.model.format" [disabled]="!canEdit() || row.markedForDelete"
                        class="w-full rounded border border-transparent px-1.5 py-1 focus:border-gray-300 focus:bg-[#fff2cc] disabled:bg-transparent disabled:text-gray-500"/>
                    </td>
                    <td class="border border-gray-300 px-2 py-1 text-gray-800">
                      <input type="text" [(ngModel)]="row.model.optionalFormat" [disabled]="!canEdit() || row.markedForDelete"
                        class="w-full rounded border border-transparent px-1.5 py-1 focus:border-gray-300 focus:bg-[#fff2cc] disabled:bg-transparent disabled:text-gray-500"/>
                    </td>
                    <td class="border border-gray-300 px-2 py-1 text-gray-800">
                      <input type="text" [(ngModel)]="row.model.outFormat" [disabled]="!canEdit() || row.markedForDelete"
                        class="w-full rounded border border-transparent px-1.5 py-1 focus:border-gray-300 focus:bg-[#fff2cc] disabled:bg-transparent disabled:text-gray-500"/>
                    </td>
                    <td class="border border-gray-300 px-3 py-2 text-center">
                      <input type="checkbox" [(ngModel)]="row.model.useCustomerLanguagePreference" [disabled]="!canEdit() || row.markedForDelete"/>
                    </td>
                    <td class="border border-gray-300 px-3 py-2 text-center">
                      <input type="checkbox" [(ngModel)]="row.model.isPhoneContact" [disabled]="!canEdit() || row.markedForDelete"/>
                    </td>
                    <td class="border border-gray-300 px-3 py-2 text-center">
                      <input type="checkbox" [(ngModel)]="row.model.ignoreHash" [disabled]="!canEdit() || row.markedForDelete"/>
                    </td>
                    <td class="border border-gray-300 px-2 py-1 text-gray-800">
                      <textarea rows="1" [ngModel]="row.translationText" (ngModelChange)="onTranslationTextChange(row, $event)"
                        [disabled]="!canEdit() || row.markedForDelete" placeholder="null"
                        class="w-full rounded border border-transparent px-1.5 py-1 font-mono text-xs focus:border-gray-300 focus:bg-[#fff2cc] disabled:bg-transparent disabled:text-gray-500"
                        [class.border-error-500]="!!row.translationError"></textarea>
                      @if (row.translationError) {
                        <span class="text-[10px] text-error-600">{{ row.translationError }}</span>
                      }
                    </td>
                  </tr>
                } @empty {
                  <tr><td [attr.colspan]="canEdit() ? 16 : 15" class="border border-gray-300 px-3 py-8 text-center text-gray-400">No CMD columns configured.</td></tr>
                }
              </tbody>
            </table>
        </div>
      } @else if (activeTab() === 'json') {
        <div>
          <h5 class="mb-4 text-lg font-semibold text-black dark:text-white">
            JSON Format
          </h5>

          <pre class="max-h-[70vh] overflow-auto whitespace-pre-wrap arap-break-words rounded bg-gray-2 p-4 text-sm dark:bg-meta-4">
            {{ survey?.cmd | json }}
          </pre>
        </div>
      }
    </div>
  </div>
</div>
}

<!-- Discard confirm -->
@if (showDiscardConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="cancelDiscard()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Discard changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }} will be lost and the CMD mapping will be restored to its last saved state.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelDiscard()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="confirmDiscard()" class="rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white hover:bg-error-700">
        Discard
      </button>
    </div>
  </div>
</div>
}

<!-- Save confirm -->
@if (showSaveConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="cancelSave()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Save changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {{ pendingChangesCount }} column{{ pendingChangesCount === 1 ? '' : 's' }} will be saved
      @if (newRowsCount > 0) {
        (<span class="text-success-600 dark:text-success-400">{{ newRowsCount }} new</span>@if (modifiedRowsCount > 0 || deletedRowsCount > 0) { , } @else { ) }
      }
      @if (modifiedRowsCount > 0) {
        <span class="text-warning-600 dark:text-warning-400">{{ modifiedRowsCount }} modified</span>@if (deletedRowsCount > 0) { , } @else if (newRowsCount > 0) { ) }
      }
      @if (deletedRowsCount > 0) {
        <span class="text-error-600 dark:text-error-400">{{ deletedRowsCount }} deleted</span>@if (newRowsCount > 0 || modifiedRowsCount > 0) { ) }
      }
    </p>
    @if (saveError()) {
    <p class="mt-3 rounded-lg bg-error-50 px-3 py-2 text-sm text-error-700 dark:bg-error-500/10 dark:text-error-400">{{ saveError() }}</p>
    }
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelSave()" [disabled]="saving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="confirmSave()" [disabled]="saving()" class="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-70">
        @if (saving()) {
          <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
          Saving...
        } @else {
          Save
        }
      </button>
    </div>
  </div>
</div>
}

<!-- Leave guard confirm -->
@if (showLeaveConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Leave page?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      You have {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }}. If you leave now they will be lost.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelLeave()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Stay
      </button>
      <button type="button" (click)="confirmLeave()" class="rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white hover:bg-error-700">
        Leave & discard
      </button>
    </div>
  </div>
</div>
}
